import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { CompanyAffiliationStep } from 'components/Flow/flows/nonprofitOnboardingCompanyAffiliation';
import { DataCaptureStep } from 'components/Flow/flows/nonprofitOnboardingDataCapture';
import { NonprofitOnboardingVerification } from 'components/Flow/flows/nonprofitOnboardingVerification';
import { paymentsRouteIsPotentiallyEnabled } from 'containers/PaymentsRoute';
import { truthy } from 'utils/truthy';
import { PaymentStep } from 'components/Flow/flows/nonprofitOnboardingPayment';
import { selectUser } from 'entities/user/selectors';
import { useNonprofitInfoQuery } from 'generated/graphql';
import { usePartners } from 'entities/organization/usePartners';
import { computeDonationProviderForNonprofit } from 'utils/computeDonationProviderForNonprofit';
import { useContent } from 'components/Flow/flows/nonprofitOnboardingPayment/components/useContent';
import { Privileges } from 'utils/privileges';

import { StepData } from './Step';

export const useSteps = () => {
  const user = useSelector(selectUser);

  const { data: nonprofitInfo, loading: nonprofitInfoLoading } = useNonprofitInfoQuery({
    skip: !user?.hasPrivilege(Privileges.ORGANIZATION_VIEW_ORGANIZERS),
  });

  const history = useHistory();

  const { data: existingPartners } = usePartners(
    { status: ['pending', 'accepted'] },
    !user?.hasPrivilege(Privileges.PARTNER_VIEW)
  );

  const donationProvider =
    user?.organization &&
    computeDonationProviderForNonprofit(
      user?.organization,
      user?.organization?.settings?.get('preferStripe') || false
    );

  const paymentModalContent = useContent(
    donationProvider,
    user?.organization,
    user?.hasPrivilege(Privileges.ORGANIZATION_SELF_VERIFIED)
  );

  if (!user?.organization) {
    return [];
  }

  if (!user.organization?.isNonprofit()) {
    return [];
  }

  if (user && !user.hasPrivilege(Privileges.ORGANIZATIONS_VIEW)) {
    return [];
  }

  const steps: StepData[] = [
    user?.organization?.locationObject?.countryCode === 'US' && {
      title: nonprofitInfo?.nonprofitInfo?.hasVerifiedUsers
        ? 'This organization has a Verified Owner'
        : 'This organization needs a Verified Owner',
      subTitle: 'Securing Your Organization with Verified Ownership',
      action: (checked) => {
        if (checked) {
          history.push('/organizers');
          return true;
        }
        return false;
      },
      ModalComponent: NonprofitOnboardingVerification,
      checked: Boolean(nonprofitInfo?.nonprofitInfo?.hasVerifiedUsers),
      loading: nonprofitInfoLoading,
      enabled: true,
    },
    paymentsRouteIsPotentiallyEnabled(user) && {
      title: paymentModalContent.header,
      onboardingTrackerTitle: 'Payments',
      subTitle: paymentModalContent.body,
      ModalComponent: PaymentStep,
      action: (checked) => {
        if (checked) {
          history.push('/payments');
          return true;
        }
        return false;
      },
      checked: paymentModalContent.enrolled,
      enabled: user.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS),
    },
    {
      title: 'Company Affiliation',
      subTitle: 'Unlock Opportunities with Company Partnerships',
      checked: existingPartners?.length > 0,
      ModalComponent: CompanyAffiliationStep,
      enabled: user.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS),
    },
    {
      title: 'Verify your Organization’s Information',
      subTitle: 'Building a Comprehensive Profile for Enhanced Engagement',
      checked: Boolean(user?.organization?.nonprofitDataValidatedAt),
      ModalComponent: DataCaptureStep,
      enabled: user.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS),
    },
  ].filter(truthy);
  return steps;
};
